import React, { useState, useEffect } from 'react'
import { apiClient } from '../api/client'
import { Modal } from './ui/Modal'
import type { Survey } from '../types'

const LANG_NAMES: Record<string, string> = {
  es: 'Spanish', fr: 'French', pt: 'Portuguese', ar: 'Arabic',
  sw: 'Swahili', hi: 'Hindi', ht: 'Haitian Creole', am: 'Amharic',
  so: 'Somali', zh: 'Chinese', de: 'German', ja: 'Japanese',
  ko: 'Korean', ru: 'Russian', it: 'Italian', nl: 'Dutch',
  tr: 'Turkish', vi: 'Vietnamese',
  lg: 'Luganda', hu: 'Hungarian',
}

interface TranslationReviewProps {
  surveyId: string
  lang: string
  survey: Survey
  onSave: () => void
  onClose: () => void
}

interface EditState {
  surveyTitle: string
  surveyDescription: string
  groups: Record<string, { name: string; description?: string }>
  questions: Record<string, { text: string; options?: string[]; rating_labels?: Record<string, string> }>
}

const TranslationReview: React.FC<TranslationReviewProps> = ({
  surveyId,
  lang,
  survey,
  onSave,
  onClose,
}) => {
  const [edits, setEdits] = useState<EditState>({
    surveyTitle: '',
    surveyDescription: '',
    groups: {},
    questions: {},
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Initialize from survey translations
  useEffect(() => {
    const t = survey.translations?.[lang]
    const questions: EditState['questions'] = {}

    for (const q of survey.questions || []) {
      const qt = q.translations?.[lang]
      if (qt) {
        questions[q.id] = {
          text: qt.text,
          options: qt.options,
          rating_labels: qt.rating_labels,
        }
      }
    }

    setEdits({
      surveyTitle: t?.title || '',
      surveyDescription: t?.description || '',
      groups: t?.groups ? { ...t.groups } : {},
      questions,
    })
  }, [survey, lang])

  const handleSave = async (approve: boolean) => {
    setSaving(true)
    setError('')
    try {
      const payload: any = {
        survey: {
          title: edits.surveyTitle,
          description: edits.surveyDescription,
          groups: edits.groups,
        },
        questions: edits.questions,
      }
      if (approve) {
        payload.status = 'approved'
      }
      await apiClient.patch(`/surveys/${surveyId}/translations/${lang}`, payload)
      onSave()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const updateQuestion = (qId: string, field: string, value: any) => {
    setEdits(prev => ({
      ...prev,
      questions: {
        ...prev.questions,
        [qId]: { ...prev.questions[qId], [field]: value },
      },
    }))
  }

  const updateOption = (qId: string, idx: number, value: string) => {
    setEdits(prev => {
      const q = prev.questions[qId]
      if (!q?.options) return prev
      const newOptions = [...q.options]
      newOptions[idx] = value
      return {
        ...prev,
        questions: { ...prev.questions, [qId]: { ...q, options: newOptions } },
      }
    })
  }

  const updateRatingLabel = (qId: string, key: string, value: string) => {
    setEdits(prev => {
      const q = prev.questions[qId]
      if (!q?.rating_labels) return prev
      return {
        ...prev,
        questions: {
          ...prev.questions,
          [qId]: { ...q, rating_labels: { ...q.rating_labels, [key]: value } },
        },
      }
    })
  }

  const updateGroup = (groupId: string, field: 'name' | 'description', value: string) => {
    setEdits(prev => ({
      ...prev,
      groups: {
        ...prev.groups,
        [groupId]: { ...prev.groups[groupId], [field]: value },
      },
    }))
  }

  const langName = LANG_NAMES[lang] || lang.toUpperCase()
  const questions = survey.questions || []
  const groups = survey.question_groups || []

  return (
    <Modal open={true} onClose={onClose} title={`Review ${langName} Translation`} className="max-w-5xl">
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
        {/* Survey title */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-muted mb-1">English (Original)</label>
            <div className="bg-background rounded-lg px-3 py-2 text-sm text-foreground border border-border-light">
              {survey.title}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-muted mb-1">{langName}</label>
            <input
              value={edits.surveyTitle}
              onChange={e => setEdits(prev => ({ ...prev, surveyTitle: e.target.value }))}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>

        {/* Survey description */}
        {survey.description && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-muted mb-1">Description (English)</label>
              <div className="bg-background rounded-lg px-3 py-2 text-sm text-foreground border border-border-light">
                {survey.description}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-muted mb-1">Description ({langName})</label>
              <textarea
                value={edits.surveyDescription}
                onChange={e => setEdits(prev => ({ ...prev, surveyDescription: e.target.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                rows={2}
              />
            </div>
          </div>
        )}

        {/* Question groups */}
        {groups.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 border-b border-border-light pb-2">
              Sections
            </h4>
            <div className="space-y-3">
              {groups.map(g => (
                <div key={g.id} className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: g.color }} />
                    <div className="bg-background rounded-lg px-3 py-2 text-sm text-foreground border border-border-light flex-1">
                      {g.name}
                    </div>
                  </div>
                  <input
                    value={edits.groups[g.id]?.name || ''}
                    onChange={e => updateGroup(g.id, 'name', e.target.value)}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Questions */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3 border-b border-border-light pb-2">
            Questions ({questions.length})
          </h4>
          <div className="space-y-4">
            {questions.map((q, idx) => {
              const group = groups.find(g => g.id === q.group_id)
              return (
                <div key={q.id} className="border border-border-light rounded-lg p-4">
                  {/* Group indicator */}
                  {group && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: group.color }} />
                      <span className="text-xs text-foreground-muted">{group.name}</span>
                    </div>
                  )}

                  {/* Question text */}
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div className="bg-background rounded-lg px-3 py-2 text-sm text-foreground border border-border-light">
                      <span className="text-xs text-foreground-muted mr-1">Q{idx + 1}.</span>
                      {q.text}
                    </div>
                    <input
                      value={edits.questions[q.id]?.text || ''}
                      onChange={e => updateQuestion(q.id, 'text', e.target.value)}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>

                  {/* Options (for choice questions) */}
                  {q.options && q.options.length > 0 && (
                    <div className="ml-4 space-y-1">
                      {q.options.map((opt, optIdx) => (
                        <div key={optIdx} className="grid grid-cols-2 gap-4">
                          <div className="bg-background rounded px-2 py-1 text-xs text-foreground-muted border border-border-light">
                            {opt}
                          </div>
                          <input
                            value={edits.questions[q.id]?.options?.[optIdx] || ''}
                            onChange={e => updateOption(q.id, optIdx, e.target.value)}
                            className="w-full border border-border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Rating labels */}
                  {q.rating_labels && Object.keys(q.rating_labels).length > 0 && (
                    <div className="ml-4 space-y-1 mt-1">
                      <span className="text-xs text-foreground-muted">Rating labels:</span>
                      {Object.entries(q.rating_labels).map(([key, label]) => (
                        <div key={key} className="grid grid-cols-2 gap-4">
                          <div className="bg-background rounded px-2 py-1 text-xs text-foreground-muted border border-border-light">
                            {key}: {label}
                          </div>
                          <input
                            value={edits.questions[q.id]?.rating_labels?.[key] || ''}
                            onChange={e => updateRatingLabel(q.id, key, e.target.value)}
                            className="w-full border border-border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Actions */}
      {error && (
        <div className="mt-4 bg-error-light border border-error text-error px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}
      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-border-light">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-foreground bg-surface border border-border hover:bg-background rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={() => handleSave(false)}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-primary border border-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
        <button
          onClick={() => handleSave(true)}
          disabled={saving}
          className="px-5 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Approve'}
        </button>
      </div>
    </Modal>
  )
}

export default TranslationReview
